import {
  TealiumEvent,
  useAnalytics,
} from 'src/components/AnalyticsProvider/AnalyticsProvider';

// Inspired from https://fettblog.eu/typescript-match-the-exact-object-shape/
type ValidateExactShape<T, Shape> = T extends Shape
  ? Exclude<keyof T, keyof Shape> extends never
    ? T
    : never
  : never;

type EventWithoutAction = Omit<TealiumEvent, 'eventaction'>;
type EventWithActionOnly = Pick<TealiumEvent, 'eventaction'>;

export type PartialEventWithRequiredAction = EventWithActionOnly &
  Partial<TealiumEvent>;

export type DefaultEventData = EventWithoutAction | TealiumEvent;

/* 1. If the default event data contains only "eventcategory",
 *    then "eventaction" is required in the arguments of the returned functions
 * 2. If the default event data contains "eventcategory" and "eventaction" (which are required in TealiumEvent),
 *    then the arguments of the returned functions can be Partial<TealiumEvent> or undefined
 * Note: In order to enable optional arguments with [undefined?] it was required to return array of argument types
 */
type EventDataTypeArgs<T> = T extends ValidateExactShape<T, EventWithoutAction>
  ? [PartialEventWithRequiredAction]
  : T extends TealiumEvent
  ? [Partial<TealiumEvent>] | [undefined?]
  : [never];

type UseSendAnalyticsReturn<T extends EventWithoutAction | TealiumEvent> = {
  sendAnalyticsBefore?: (
    cb: () => void,
    ...eventData: EventDataTypeArgs<T>
  ) => void;
  sendAnalytics?: (...eventData: EventDataTypeArgs<T>) => void;
  sendAnalyticsWithDelay?: (
    ...eventData: EventDataTypeArgs<T>
  ) => Promise<void>;
};

export const ANALYTICS_TIMEOUT = 200;

export function useSendAnalytics<T extends DefaultEventData>(
  eventDataDefault: T
): UseSendAnalyticsReturn<T> {
  const { sendTealiumEvent } = useAnalytics();

  function sendAnalyticsBefore(
    cb: () => void,
    ...eventData: EventDataTypeArgs<T>
  ): void {
    sendAnalytics(...eventData);
    cb();
  }

  function sendAnalytics(...eventData: EventDataTypeArgs<T>): void {
    sendTealiumEvent({
      ...eventDataDefault,
      ...(eventData[0] || {}),
    } as TealiumEvent);
  }

  function sendAnalyticsWithDelay(
    ...eventData: EventDataTypeArgs<T>
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      sendAnalytics(...eventData);
      setTimeout(resolve, ANALYTICS_TIMEOUT);
    });
  }

  return {
    sendAnalyticsBefore,
    sendAnalytics,
    sendAnalyticsWithDelay,
  };
}
